import { select, call, takeLatest, put, all } from 'redux-saga/effects';
import { ReactNode } from 'react';
import { PayrollActions } from '../constants';
import { PayrollApi } from 'src/services/end-points';
import { StoreState } from '../reducers';
import { PaginationService } from '../../services/api-handlers/pagination';
import {
    DeletePayrollAction,
    GetPayrollAction,
    GetPayrollsAction,
    GetProvidersPayrollsAction,
    PersistPayrollAction,
    GetPayrollsEntitiesAction,
    UpdatePayrollAction,
} from '../actions/payroll-actions';
import sortingService from '../../services/sorting';
import {
    failureType,
    handleError,
    requestType,
    resolveApiCall,
    ResolverApiFailure,
    ResolverApiSuccess,
    successType,
} from '../../services/api-handlers/api-resolver';
import { SubmissionError } from 'redux-form';
import { makeFormErrors } from '../../components/redux-form/utils/make-form-errors';
import { push } from 'connected-react-router';
import Router from '../../navigation/router';
import routes from '../../navigation/routes';
import { groupPayroll } from 'src/helpers/payroll/payroll';
import { groupDataToCsv } from '../../helpers/payroll/groupCsv';
import { exportToCsv } from '../../services/exports/csv';
import { GroupPayroll } from 'src/screens/General/screens/Providers/screens/ProviderDetail/tabs/Payrolls/helpers/GroupPayroll';
import { toast } from 'react-toastify';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { changeToZeroMoment } from '../../helpers/timeHelpers';

function* handleFetchPayrolls(action: GetPayrollsAction) {
    const oldData = yield select((state: StoreState) => state.payrolls.data);
    const { filters, sorting, page, itemsPerPage } = action.payload;
    let filterProps: any = { ...filters };

    if (filterProps?.payrollDate) {
        filterProps = {
            ...filters,
            ...filterProps?.payrollDate,
        };
        delete filterProps.payrollDate;
    }
    yield PaginationService.setPageSize(itemsPerPage).resolveApiCall(action, page, oldData, () =>
        PayrollApi.getPayrolls({
            ...filterProps,
            order: sortingService.makeOrder(sorting),
            page,
            itemsPerPage,
            pagination: itemsPerPage === -1 ? 0 : 1,
        }),
    );
}

function* handleFetchPayrollCsv(action: GetPayrollsAction) {
    const { filters, sorting, page, itemsPerPage } = action.payload;
    let filterProps: any = { ...filters };

    if (filterProps?.payrollDate) {
        filterProps = {
            ...filters,
            ...filterProps?.payrollDate,
        };
        delete filterProps.payrollDate;
    }

    yield resolveApiCall(
        action,
        () =>
            PayrollApi.getPayrolls({
                ...filterProps,
                order: sortingService.makeOrder(sorting),
                page,
                itemsPerPage: 5000,
                pagination: itemsPerPage === -1 ? 0 : 1,
            }),

        function (data) {
            const groupCsvData = groupDataToCsv(data.payload);
            exportToCsv('payroll.csv', groupCsvData);
        },
        function (result: ResolverApiFailure) {
            if (result.error && result.error.response && result.error.response.status) {
                toast.error(result.error.response.data.detail ?? result.error.message ?? 'Unknown error');
            }
            handleError(result);
        },
    );
}

function* handleFetchPayrollInFile(action: GetPayrollsAction) {
    const { filters, sorting, page, itemsPerPage } = action.payload;
    let filterProps: any = { ...filters };

    if (filterProps?.payrollDate) {
        filterProps = {
            ...filters,
            ...filterProps?.payrollDate,
        };
        delete filterProps.payrollDate;
    }

    yield put({ type: requestType(PayrollActions.FETCH_PAYROLLS_IN_FILE) });

    try {
        const response = yield call(PayrollApi.getPayrolls, {
            ...filterProps,
            order: sortingService.makeOrder(sorting),
            page,
            itemsPerPage: 5000,
            pagination: itemsPerPage === -1 ? 0 : 1,
        });

        const zip = new JSZip();

        const tasks = response.data.map((item: any) =>
            call(function* () {
                if (!item.attach) return;

                const url = item.attach.replace('https://localhost', 'http://localhost:10100');
                let filename = `${item.provider.fullName}_from_${changeToZeroMoment(
                    item.periodStart,
                    'D_M_Y',
                )}_to_${changeToZeroMoment(item.periodEnd, 'D_M_Y')}_p${item.id}.csv`;
                filename = filename.replace(/[\s-]/g, '');

                try {
                    const fileResponse: Response = yield call(fetch, url);
                    const blob: Blob = yield call([fileResponse, fileResponse.blob]);
                    const arrayBuffer = yield call([blob, blob.arrayBuffer]);

                    zip.file(filename, arrayBuffer);
                } catch (err) {
                    console.error(`Failed to fetch file from ${url}`, err);
                }
            }),
        );

        yield all(tasks);

        const zipBlob = yield call([zip, zip.generateAsync], { type: 'blob' });
        yield call(saveAs, zipBlob, 'payrolls.zip');

        yield put({ type: successType(PayrollActions.FETCH_PAYROLLS_IN_FILE) });
    } catch (error) {
        yield put({
            type: failureType(PayrollActions.FETCH_PAYROLLS_IN_FILE),
            error: error.message || 'Unknown error',
        });

        handleError(error);
    }
}
function* handleFetchPayrollInFileOneByOne(action: GetPayrollsAction) {
    const { filters, sorting, page, itemsPerPage } = action.payload;

    // Manually dispatch request
    yield put({ type: requestType(PayrollActions.FETCH_PAYROLLS_IN_FILE) });

    try {
        const response = yield call(PayrollApi.getPayrolls, {
            ...filters,
            order: sortingService.makeOrder(sorting),
            page,
            itemsPerPage: 5000,
            pagination: itemsPerPage === -1 ? 0 : 1,
        });

        const zip = new JSZip();

        for (let i = 0; i < response.data.length; i++) {
            const item = response.data[i];
            if (item.attach) {
                const url = item.attach.replace('https://localhost', 'http://localhost:10100');
                let filename = `${item.provider.fullName}_from_${changeToZeroMoment(
                    item.periodStart,
                    'D_M_Y',
                )}_to_${changeToZeroMoment(item.periodEnd, 'D_M_Y')}_p${item.id}.csv`;
                filename = filename.replace(/[\s-]/g, '');

                try {
                    const fileResponse: Response = yield call(fetch, url);
                    const blob: Blob = yield call([fileResponse, fileResponse.blob]);
                    const arrayBuffer = yield call([blob, blob.arrayBuffer]);

                    zip.file(filename, arrayBuffer);
                } catch (err) {
                    console.error(`Failed to fetch file from ${url}`, err);
                }
            }
        }

        const zipBlob = yield call([zip, zip.generateAsync], { type: 'blob' });
        yield call(saveAs, zipBlob, 'payrolls.zip');

        // Now dispatch success only AFTER the zip is saved
        yield put({ type: successType(PayrollActions.FETCH_PAYROLLS_IN_FILE) });
    } catch (error) {
        yield put({
            type: failureType(PayrollActions.FETCH_PAYROLLS_IN_FILE),
            error: error.message || 'Unknown error',
        });

        handleError(error);
    }
}

function* handleFetchPayrollsEntities(action: GetPayrollsEntitiesAction) {
    const { id } = action.payload;
    yield put({
        type: requestType(PayrollActions.FETCH_PAYROLLS_ENTITIES),
    });
    let resData: { [key: string]: string | ReactNode }[] = [];

    const p1 = PayrollApi.getPayrollsEntities(id, {}, 'payroll_advances');
    const p2 = PayrollApi.getPayrollsEntities(id, {}, 'payroll_payments');
    const p3 = PayrollApi.getPayrollsEntities(id, {}, 'payroll_admin_works');
    const p4 = PayrollApi.getPayrollsEntities(id, {}, 'payroll_expenses');
    const p5 = PayrollApi.getPayrollsEntities(id, {}, 'payroll_internals');
    const p6 = PayrollApi.getPayrollsEntities(id, {}, 'payroll_deductions');
    const objKeys: any = {
        0: 'payroll_advances',
        1: 'payroll_payments',
        2: 'payroll_admin_works',
        3: 'payroll_expenses',
        4: 'payroll_internals',
        5: 'payroll_deductions',
    };

    const dataProm = yield all([p1, p2, p3, p4, p5, p6]);

    dataProm.forEach((it: any, key: number) => {
        const dataCurrent = GroupPayroll({ type: objKeys[key], list: it.data });
        resData = resData.concat(dataCurrent);
    });

    const actionResult = {
        payload: resData,
        type: successType(PayrollActions.FETCH_PAYROLLS_ENTITIES),
    };

    yield put(actionResult);
}

function* handleGetProvidersPayroll(action: GetProvidersPayrollsAction) {
    //const oldData = yield select((state: StoreState) => state.payrolls.data);
    const { payload } = action;

    yield resolveApiCall(
        action,
        () =>
            PayrollApi.getProvidersPayrolls({
                page: 1,
                ...payload,
            }),
        function () {
            /* if (onSuccess) {
                onSuccess();
            }*/
        },
        function (result: ResolverApiFailure) {
            if (result.error && result.error.response && result.error.response.status) {
                toast.error(result.error.response.data.detail ?? result.error.message ?? 'Unknown error');
            }
            handleError(result);
        },
    );
}

function* handleFetchPayroll(action: GetPayrollAction) {
    const {
        payload: { id },
    } = action;

    yield resolveApiCall(action, () => PayrollApi.getPayroll(id));
}

function* handleDeletePayroll(action: DeletePayrollAction) {
    const {
        payload: { id, onSuccess, onError },
    } = action;

    yield resolveApiCall(
        action,
        () => PayrollApi.deletePayroll(id),
        function () {
            if (onSuccess) {
                onSuccess();
            }
        },
        function (result: ResolverApiFailure) {
            if (result.error && result.error.response && result.error.response.status) {
                if (onError) {
                    onError(result.error.response.data.detail ?? result.error.message ?? 'Unknown error');
                }
            }
            handleError(result);
        },
    );
}

function* handlePersistPayroll(action: PersistPayrollAction) {
    const { payload } = action;
    const {
        data: { id, ...restData },
        callbacks,
    } = payload;
    const groupedData = groupPayroll(restData);
    yield resolveApiCall(
        action,
        () => (id ? PayrollApi.updatePayroll(id, restData) : PayrollApi.createPayroll(groupedData)),
        function* (data: ResolverApiSuccess) {
            yield call(callbacks.resolve);
            if (!id) {
                yield put(push(Router.generate(routes.PAYROLLS_LIST)));
            }
        },
        function* (result: ResolverApiFailure) {
            if (result.error && result.error.response && result.error.response.status) {
                yield call(
                    callbacks.reject,
                    result.error instanceof SubmissionError
                        ? result.error
                        : new SubmissionError({
                              _error: result.error.message ?? 'Incorrect response',
                              ...makeFormErrors(result.error.response?.data),
                          }),
                );
            }
            handleError(result);
        },
    );
}
function* handleUpdatePayrollStatus(action: UpdatePayrollAction) {
    const {
        payload: { data, onSuccess, onError },
    } = action;

    const { id, noReload, ...restDate } = data;
    yield resolveApiCall(
        action,
        () => PayrollApi.updatePayrollStatus(`${id}`, restDate),
        function* () {
            if (!noReload) {
                const actionGet: GetPayrollsAction = {
                    type: PayrollActions.FETCH_PAYROLLS,
                    payload: {
                        page: 1,
                        itemsPerPage: 30,
                    },
                };
                yield call(handleFetchPayrolls, actionGet);
            }
            if (onSuccess) {
                yield put({ type: successType(PayrollActions.UPDATE_PAYROLL), payload: { id: id, status: 3 } });
                onSuccess();
            }
        },
        function* (result: ResolverApiFailure) {
            yield put({ type: failureType(PayrollActions.UPDATE_PAYROLL), error: result?.error?.message });
            if (result.error && result.error.response && result.error.response.status) {
                if (onError) {
                    onError(result.error?.response?.data?.detail ?? 'Unknown error');
                }
            }
            handleError(result);
        },
    );
}

export default function* () {
    yield takeLatest(PayrollActions.FETCH_PAYROLLS, handleFetchPayrolls);
    yield takeLatest(PayrollActions.FETCH_PAYROLLS_CSV, handleFetchPayrollCsv);
    yield takeLatest(PayrollActions.FETCH_PAYROLLS_IN_FILE, handleFetchPayrollInFile);
    yield takeLatest(PayrollActions.FETCH_PAYROLLS_ENTITIES, handleFetchPayrollsEntities);
    yield takeLatest(PayrollActions.FETCH_PAYROLL, handleFetchPayroll);
    yield takeLatest(PayrollActions.DELETE_PAYROLL, handleDeletePayroll);
    yield takeLatest(PayrollActions.PERSIST_PAYROLL, handlePersistPayroll);
    yield takeLatest(PayrollActions.UPDATE_PAYROLL, handleUpdatePayrollStatus);
    yield takeLatest(PayrollActions.FETCH_PAYROLLS_PROVIDERS, handleGetProvidersPayroll);
}
